import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useNavigate, Link, useLocation } from 'react-router-dom'; // Add useLocation

import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  TextField,
  Modal,
  Fade,
  Backdrop,
  Slide,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction
  } from '@mui/material';
import {
  Info as InfoIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import TradeForm from './TradeForm';
import DetailedPsychologyReport from './DetailedPsychologyReport';

import TradeCharts from './TradeCharts';
import Insights from './Insights';
import RiskManagement from './RiskManagement';
import { calculateAnalysis } from '../utils';
import PsychologyIcon from '@mui/icons-material/Psychology'; // New icon for analysis
import { 
  FaChartLine, 
  FaBrain, 
  FaExclamationTriangle,
  FaCheckCircle,
  FaLightbulb,
  FaBullseye,
  FaSignOutAlt,
  FaCog,
  FaPlus,
  FaBars,
  FaHome,
  FaUser
} from 'react-icons/fa';
import { 
  MdDashboard, 
  MdAssessment, 
  MdPsychology,
  MdInfo,
  MdWarning,
  MdClose
} from 'react-icons/md';
import ChatIcon from '@mui/icons-material/Chat'; // Import Chat icon from @mui/icons-material
import { Notes } from '@mui/icons-material'; // Import Notes icon
import InstallPrompt from './InstallPrompt'; // Import InstallPrompt for PWA

const Dashboard = () => {
  const [trades, setTrades] = useState([]);
  const [analysis, setAnalysis] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [profitTargets, setProfitTargets] = useState({
    initialBalance: 10000,
    profitTarget: 0,
    weeklyProfitTarget: 0,
    monthlyProfitTarget: 0,
    quarterlyProfitTarget: 0,
    yearlyProfitTarget: 0,
  });
  const [newProfitTargets, setNewProfitTargets] = useState({
    initialBalance: 10000,
    profitTarget: 0,
    weeklyProfitTarget: 0,
    monthlyProfitTarget: 0,
    quarterlyProfitTarget: 0,
    yearlyProfitTarget: 0,
  });
  const [isTargetsModalOpen, setIsTargetsModalOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const navigate = useNavigate();
  const [weeklyCheckResult, setWeeklyCheckResult] = useState(null);
  const [accountBlown, setAccountBlown] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const [psychologyAnalysis, setPsychologyAnalysis] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showMentalCheck, setShowMentalCheck] = useState(false);
  const [lastTrade, setLastTrade] = useState(null);
  const [previousSession, setPreviousSession] = useState(null);
  const [showDetailedPsychologyModal, setShowDetailedPsychologyModal] = useState(false);

  const [showTradingOverview, setShowTradingOverview] = useState(true);
  const [showRiskManagement, setShowRiskManagement] = useState(true);
  const [showTradeLog, setShowTradeLog] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [showWeeklyTargetPopup, setShowWeeklyTargetPopup] = useState(() => {
    // Check if popup was already dismissed for this week
    const dismissedWeek = localStorage.getItem('weeklyTargetPopupDismissed');
    const currentWeek = new Date().toISOString().split('T')[0].slice(0, 10); // YYYY-MM-DD format
    return dismissedWeek !== currentWeek;
  });
  
  // Prevent multiple simultaneous API calls
  const fetchingRef = useRef({
    user: false,
    trades: false,
    session: false,
    psychology: false
  });


  

  
  // Mobile navigation states
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bottomNavValue, setBottomNavValue] = useState(0);
  
  // Theme and responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();



  // Define functions with useCallback to prevent recreation
  const fetchUser = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (fetchingRef.current.user) {
      console.log('🚫 fetchUser already in progress, skipping...');
      return;
    }
    
    fetchingRef.current.user = true;
    
    try {
      const res = await api.get('/api/auth/user');
      setUser(res.data);
      
      // Fetch profit targets
      const profitTargetsRes = await api.get('/api/auth/profit-targets');
      setProfitTargets(profitTargetsRes.data);
      setNewProfitTargets(profitTargetsRes.data);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      
      // Handle rate limiting gracefully
      if (err.response?.status === 429) {
        console.warn('⚠️ Dashboard: Rate limited, will retry later');
        setError('Server is busy, please wait a moment...');
        // Retry after 5 seconds
        setTimeout(() => {
          if (!fetchingRef.current.user) {
            fetchUser();
          }
        }, 5000);
      } else if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      } else {
        setError('Failed to fetch user information. Please refresh the page.');
      }
    } finally {
      fetchingRef.current.user = false;
    }
  }, [navigate]);

  const fetchTradingSession = useCallback(async () => {
    if (fetchingRef.current.session) {
      console.log('🚫 fetchTradingSession already in progress, skipping...');
      return;
    }
    
    // Check network status before making request
    if (!navigator.onLine) {
      console.log('🌐 Network offline, skipping trading session fetch');
      return;
    }
    
    fetchingRef.current.session = true;
    
    try {
      const response = await api.get('/api/psychology/trading-session');
      const newSession = response.data;
      
      // Play bell sound when session changes
      if (previousSession && newSession.activeSessions.length > 0) {
        // Check if any new session started
        const newActiveSessions = newSession.activeSessions.filter(session =>
          !previousSession.activeSessions.some(prev => prev.key === session.key)
        );
        
        if (newActiveSessions.length > 0) {
          // Play sound for the first new session
          const newSessionKey = newActiveSessions[0].key;
          playSessionBell(newSessionKey);
        }
      }
      
      setPreviousSession(newSession);
    } catch (err) {
      console.error('Failed to fetch trading session:', err);
      
      // Handle different error types appropriately
      if (err.message === 'Network is offline. Please check your connection.') {
        console.log('🌐 Network offline, will retry when connection restored');
      } else if (err.response?.status === 429) {
        console.warn('⚠️ Dashboard: Trading session fetch rate limited');
      } else if (err.response?.status === 401) {
        console.warn('🔒 Unauthorized, token may need refresh');
      } else {
        console.error('❌ Unexpected error fetching trading session:', err);
      }
    } finally {
      fetchingRef.current.session = false;
    }
  }, [previousSession]);

  const fetchPsychologyAnalysis = useCallback(async (forceRefresh = false) => {
    if (fetchingRef.current.psychology && !forceRefresh) {
      console.log('🚫 fetchPsychologyAnalysis already in progress, skipping...');
      return;
    }
    
    // Check network status before making request
    if (!navigator.onLine) {
      console.log('🌐 Network offline, skipping psychology analysis fetch');
      return;
    }
    
    fetchingRef.current.psychology = true;
    
    try {
      console.log('🔍 Fetching psychology analysis...');
      // Add timestamp to force fresh request
      const timestamp = Date.now();
      const response = await api.get(`/api/psychology/analysis?t=${timestamp}`);
      console.log('✅ Psychology analysis response:', response.data);
      setPsychologyAnalysis(response.data);
    } catch (err) {
      console.error('❌ Failed to fetch psychology analysis:', err);
      
      // Handle different error types appropriately
      if (err.message === 'Network is offline. Please check your connection.') {
        console.log('🌐 Network offline, will retry when connection restored');
      } else if (err.response?.status === 429) {
        console.warn('⚠️ Dashboard: Psychology analysis fetch rate limited');
      } else if (err.response?.status === 401) {
        console.warn('🔒 Unauthorized, token may need refresh');
      } else {
        console.error('❌ Unexpected error fetching psychology analysis:', err);
      }
    } finally {
      fetchingRef.current.psychology = false;
    }
  }, []);



  const handleMediaClick = (media) => {
    setSelectedMedia(media);
    setIsMediaModalOpen(true);
  };

  const handleCloseMediaModal = () => {
    setSelectedMedia(null);
    setIsMediaModalOpen(false);
  };

  useEffect(() => {
    // Only run on mount, not when previousSession changes
    fetchUser();
    fetchTrades();
    fetchTradingSession();
    fetchPsychologyAnalysis();
    
    // Update trading session every 5 minutes instead of every minute to reduce API calls
            const sessionInterval = setInterval(fetchTradingSession, 600000); // 10 minutes
    
    return () => {
      clearInterval(sessionInterval);
    };
    // Remove previousSession dependency to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh psychology analysis when component becomes visible (e.g., when returning from psychology assessment)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && navigator.onLine) { // Only refresh if online
        // Force refresh psychology analysis when returning to dashboard
        fetchPsychologyAnalysis();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchPsychologyAnalysis]);

  // Add network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Network: Back online, refreshing data...');
      setIsOnline(true);
      // Only refresh essential data when coming back online
      fetchUser();
      fetchTradingSession();
    };

    const handleOffline = () => {
      console.log('🌐 Network: Gone offline, pausing API calls...');
      setIsOnline(false);
      // Clear any pending intervals when offline
      if (window.sessionInterval) {
        clearInterval(window.sessionInterval);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchUser, fetchTradingSession]);

  // Show welcome popup when user data is loaded
  useEffect(() => {
    if (user && showWelcomePopup) {
      // Mark popup as shown in localStorage
      localStorage.setItem('welcomePopupShown', 'true');
      
      // Auto-hide after 4 seconds
      const timer = setTimeout(() => {
        setShowWelcomePopup(false);
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [user, showWelcomePopup]);

  // Scroll animations
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setIsScrolling(true);
      
      // Clear scrolling state after scroll ends
      clearTimeout(window.scrollTimeout);
      window.scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(window.scrollTimeout);
    };
  }, []);

  // Click animations
  const handleClickAnimation = (element) => {
    if (element) {
      element.style.transform = 'scale(0.95)';
      element.style.transition = 'transform 0.1s ease-out';
      
      setTimeout(() => {
        element.style.transform = 'scale(1)';
      }, 100);
    }
  };

  const fetchTrades = async () => {
    if (fetchingRef.current.trades) {
      console.log('🚫 fetchTrades already in progress, skipping...');
      return;
    }
    
    fetchingRef.current.trades = true;
    
    try {
      const res = await api.get('/api/trades');
      const sortedTrades = res.data.sort((a, b) => new Date(a.closeDate) - new Date(b.closeDate));
      setTrades(sortedTrades);
      setAnalysis(calculateAnalysis(sortedTrades, profitTargets.initialBalance));
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch trades:', err);
      if (err.response?.status === 429) {
        console.warn('⚠️ Dashboard: Trades fetch rate limited, will retry later');
      } else if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      } else {
        setError('Failed to fetch trades. Please refresh the page.');
        setLoading(false);
      }
    } finally {
      fetchingRef.current.trades = false;
    }
  };

  const playSessionBell = (sessionKey) => {
    try {
      // Play actual audio files
      const audio = new Audio(`/sounds/${sessionKey}_bell.txt`);
      audio.play().catch(e => console.log("Audio play failed:", e));
    } catch (err) {
      console.error('Failed to play session bell:', err);
    }
  };

  // Function to get psychology data for a specific week
  const getWeeklyPsychologyData = useCallback(async (startDate, endDate) => {
    try {
      const response = await api.get('/api/psychology/checks');
      const psychologyChecks = response.data || [];
      
      // Filter psychology checks for the week
      const weeklyPsychologyChecks = psychologyChecks.filter(check => {
        const checkDate = new Date(check.date);
        return checkDate >= startDate && checkDate <= endDate;
      });
      
      return weeklyPsychologyChecks;
    } catch (err) {
      console.error('Failed to fetch weekly psychology data:', err);
      return [];
    }
  }, []);

  // Function to analyze psychology impact on performance
  const analyzePsychologyImpact = (psychologyChecks, weeklyTrades) => {
    if (psychologyChecks.length === 0 || weeklyTrades.length === 0) {
      return {
        impact: 'No psychology data available for analysis',
        factors: [],
        recommendations: []
      };
    }

    const avgPsychologyScore = psychologyChecks.reduce((sum, check) => sum + check.score, 0) / psychologyChecks.length;
    const losingTrades = weeklyTrades.filter(trade => trade.pnl < 0);
    const winningTrades = weeklyTrades.filter(trade => trade.pnl > 0);
    
    let impact = '';
    let factors = [];
    let recommendations = [];

    // Analyze psychology score impact
    if (avgPsychologyScore < 40) {
      impact = 'Poor psychological state significantly impacted trading performance';
      factors.push('Low psychology scores (below 40)');
      recommendations.push('Complete daily psychology assessments and address mental state before trading');
    } else if (avgPsychologyScore < 60) {
      impact = 'Suboptimal psychological state affected trading decisions';
      factors.push('Below-average psychology scores (40-60)');
      recommendations.push('Improve psychological preparation and emotional control');
    } else if (avgPsychologyScore < 75) {
      impact = 'Moderate psychological state with room for improvement';
      factors.push('Average psychology scores (60-75)');
      recommendations.push('Continue psychological development for better performance');
    } else {
      impact = 'Good psychological state supported trading performance';
      factors.push('Strong psychology scores (75+)');
    }

    // Analyze specific psychology factors
    psychologyChecks.forEach(check => {
      const data = check.answers || check;
      
      if (data.stressLevel && data.stressLevel > 7) {
        factors.push('High stress levels detected');
        recommendations.push('Implement stress management techniques (breathing exercises, breaks)');
      }
      
      if (data.sleepHours && data.sleepHours < 6) {
        factors.push('Insufficient sleep identified');
        recommendations.push('Prioritize 7-9 hours of quality sleep');
      }
      
      if (data.confidenceLevel && data.confidenceLevel < 5) {
        factors.push('Low confidence levels');
        recommendations.push('Review successful trades and build confidence gradually');
      }
      
      if (data.focusLevel && data.focusLevel < 6) {
        factors.push('Poor focus levels');
        recommendations.push('Create distraction-free trading environment');
      }
    });

    // Analyze trading patterns vs psychology
    if (losingTrades.length > winningTrades.length && avgPsychologyScore < 60) {
      factors.push('More losing trades during poor psychological periods');
      recommendations.push('Avoid trading when psychology score is below 60');
    }

    return {
      impact,
      factors: [...new Set(factors)], // Remove duplicates
      recommendations: [...new Set(recommendations)] // Remove duplicates
    };
  };

  // Function to check if user met their weekly target based on South African calendar
  const checkWeeklyTarget = useCallback(async () => {
    if (!trades || trades.length === 0 || !profitTargets.weeklyProfitTarget) {
      return;
    }

    // Get current date in South African timezone
    const now = new Date();
    const saTime = new Intl.DateTimeFormat('en-za', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(now);
    
    // Parse the SA date to get the start of the week (Monday)
    const [day, month, year] = saTime.split('/');
    const currentDate = new Date(`${year}-${month}-${day}`);
    const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Calculate the start of the week (Monday) and end of the week (Sunday) in SA time
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1)); // Adjust to Monday
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
    endOfWeek.setHours(23, 59, 59, 999);

    // Filter trades from the current week based on SA time
    const weeklyTrades = trades.filter(trade => {
      const tradeDate = new Date(trade.closeDate);
      return tradeDate >= startOfWeek && tradeDate <= endOfWeek;
    });

    // Calculate total P&L for weekly trades
    const weeklyPnl = weeklyTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0);

    // Check if target is met
    const targetMet = weeklyPnl >= profitTargets.weeklyProfitTarget;

    // Show weekly target popup from Friday (5) to Sunday (0) afternoon
    // Friday = 5, Saturday = 6, Sunday = 0
    const shouldShowPopup = dayOfWeek >= 5 || dayOfWeek === 0;

    // Get psychology data for the week
    const weeklyPsychologyChecks = await getWeeklyPsychologyData(startOfWeek, endOfWeek);
    
    // Analyze psychology impact on performance
    const psychologyAnalysis = analyzePsychologyImpact(weeklyPsychologyChecks, weeklyTrades);

    // Generate recommendations if target not met
    let recommendations = [];
    if (!targetMet) {
      const analysis = calculateAnalysis(weeklyTrades, profitTargets.initialBalance);
      
      if (analysis.winRate < 50) {
        recommendations.push("Your win rate is below 50%. Focus on improving your setup quality and only take high-probability trades.");
      }
      
      if (analysis.avgRiskReward < 1.5) {
        recommendations.push("Your risk/reward ratio is below 1.5. Aim for setups with at least 2:1 risk/reward ratio.");
      }
      
      if (analysis.qtyStd > 0.5) {
        recommendations.push("Your position sizing is inconsistent. Try to standardize your position size to reduce risk.");
      }
      
      if (analysis.lowPnlCount > weeklyTrades.length / 2) {
        recommendations.push("Too many low P&L trades. Focus on capturing bigger moves with stronger setups.");
      }
      
      if (recommendations.length === 0) {
        recommendations.push("You're doing well overall, but try to increase your trade frequency or target larger moves.");
      }
    }

    // Combine trading and psychology recommendations
    const allRecommendations = [...recommendations, ...psychologyAnalysis.recommendations];

    setWeeklyCheckResult({
      targetMet,
      recentPnl: weeklyPnl,
      target: profitTargets.weeklyProfitTarget,
      recommendations: allRecommendations,
      weekStart: startOfWeek,
      weekEnd: endOfWeek,
      shouldShowPopup,
      psychologyAnalysis,
      weeklyTradesCount: weeklyTrades.length,
      psychologyChecksCount: weeklyPsychologyChecks.length
    });
  }, [trades, profitTargets, getWeeklyPsychologyData]);

  // Check weekly target when trades or profit targets change
  useEffect(() => {
    const runChecks = async () => {
      await checkWeeklyTarget();
      checkAccountBlown();
    };
    runChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades, profitTargets]);

  // Function to check if account is blown (lost more than 80% of initial balance)
  const checkAccountBlown = useCallback(() => {
    if (!trades || trades.length === 0 || !profitTargets.initialBalance) {
      setAccountBlown(false);
      return;
    }

    const totalPnl = trades.reduce((sum, trade) => sum + (trade.pnl || 0), 0);
    const currentBalance = profitTargets.initialBalance + totalPnl;
    const blownThreshold = profitTargets.initialBalance * 0.2; // 20% of initial balance

    setAccountBlown(currentBalance <= blownThreshold);
  }, [trades, profitTargets]);

  // Function to reset account
  const handleResetAccount = async () => {
    try {
      // Delete all trades
      await api.delete('/api/trades');
      
      // Clear all trades from state
      setTrades([]);
      setAnalysis(calculateAnalysis([], profitTargets.initialBalance));
      setAccountBlown(false);
      setWeeklyCheckResult(null);
      setIsResetModalOpen(false);
      
      // Show success message
      setError('');
      alert('Account reset successfully! You can now start fresh with your trading journal.');
    } catch (err) {
      console.error('Failed to reset account:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      } else {
        setError('Failed to reset account. Please try again.');
      }
    }
  };

  const handleAddTrade = (newTrade) => {
    const updatedTrades = [newTrade, ...trades].sort((a, b) => new Date(a.closeDate) - new Date(b.closeDate));
    setTrades(updatedTrades);
    setAnalysis(calculateAnalysis(updatedTrades, profitTargets.initialBalance));
    
    // Show mental capacity check modal after trade submission
    setLastTrade(newTrade);
    setShowMentalCheck(true);
  };



  const handleDeleteTrade = async (tradeId) => {
    try {
      await api.delete(`/api/trades/${tradeId}`);
      
      // Remove the trade from state
      const updatedTrades = trades.filter(trade => trade._id !== tradeId); // Already sorted
      setTrades(updatedTrades);
      setAnalysis(calculateAnalysis(updatedTrades, profitTargets.initialBalance));
    } catch (err) {
      console.error('Failed to delete trade:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      } else {
        setError('Failed to delete trade. Please try again.');
      }
    }
  };

  const handleDeleteAllTrades = async () => {
    // Confirm with user before deleting all trades
    if (!window.confirm('Are you sure you want to delete all trades? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete('/api/trades');
      
      // Clear all trades from state
      setTrades([]);
      setAnalysis(calculateAnalysis([], profitTargets.initialBalance));
    } catch (err) {
      console.error('Failed to delete all trades:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      } else {
        setError('Failed to delete all trades. Please try again.');
      }
    }
  };

  const handleEditTrade = (trade) => {
    setEditingTrade(trade);
    // Scroll to the trade form
    const tradeFormElement = document.getElementById('trade-form');
    if (tradeFormElement) {
      tradeFormElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleUpdateTrade = (updatedTrade) => {
    const updatedTrades = trades.map(trade => 
      trade._id === updatedTrade._id ? updatedTrade : trade
    ).sort((a, b) => new Date(a.closeDate) - new Date(b.closeDate));
    setTrades(updatedTrades);
    setAnalysis(calculateAnalysis(updatedTrades, profitTargets.initialBalance));
    setEditingTrade(null);
  };

  const handleCancelEdit = () => {
    setEditingTrade(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const updateProfitTarget = async (targetType) => {
    try {
      const updateData = {};
      updateData[targetType] = newProfitTargets[targetType];
      
      const res = await api.put('/api/auth/profit-targets', updateData);
      
      setProfitTargets(res.data);
      setNewProfitTargets({...newProfitTargets, ...updateData});
    } catch (err) {
      console.error('Failed to update profit target:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      } else {
        setError('Failed to update profit target. Please try again.');
      }
    }
  };

  const exportCSV = () => {
    const csv = Papa.unparse(trades);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `trades_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Trader Journal - Trade Report', 10, 10);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 10, 20);
    doc.text(`Total P&L: $${analysis.totalPnl?.toFixed(2) || 0}`, 10, 30);
    doc.text(`Win Rate: ${analysis.winRate?.toFixed(2) || 0}%`, 10, 40);
    
    let yPos = 60;
    trades.forEach((trade, i) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(
        `Trade ${i + 1}: ${trade.symbol} ${trade.side} - P&L: $${trade.pnl.toFixed(2)} - ${trade.status}`,
        10,
        yPos
      );
      yPos += 10;
    });
    
    doc.save(`trades_report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const columns = [
    { field: 'tradeId', headerName: 'Trade ID', width: 100 },
    {
      field: 'openDate',
      headerName: 'Open Date',
      width: 150,
      valueFormatter: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      field: 'closeDate',
      headerName: 'Close Date',
      width: 150,
      valueFormatter: ({ value }) => new Date(value).toLocaleString(),
    },
    { field: 'symbol', headerName: 'Symbol', width: 100 },
    { field: 'side', headerName: 'Side', width: 80 },
    { field: 'entry', headerName: 'Entry', width: 100, type: 'number' },
    { field: 'exit', headerName: 'Exit', width: 100, type: 'number' },
    { field: 'qty', headerName: 'Qty', width: 80, type: 'number' },
    {
      field: 'pnl',
      headerName: 'P&L',
      width: 100,
      type: 'number',
      valueFormatter: ({ value }) => `$${value.toFixed(2)}`,
      cellClassName: (params) => (params.value >= 0 ? 'profit' : 'loss'),
    },
    { field: 'status', headerName: 'Status', width: 80 },
    { field: 'setupQuality', headerName: 'Setup', width: 80 },
    { field: 'marketCondition', headerName: 'Market', width: 120 },
    { field: 'notes', headerName: 'Notes', width: 200 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSelectedTrade(params.row);
              setIsTradeModalOpen(true);
            }}
          >
            View
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEditTrade(params.row)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleDeleteTrade(params.row._id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];



  // Mobile navigation functions
  const handleBottomNavChange = (event, newValue) => {
    setBottomNavValue(newValue);
    switch (newValue) {
      case 0: // Home
        navigate('/dashboard');
        break;
      case 1: // Add Trade
        scrollToSection('trade-form');
        break;
      case 2: // Chat
        navigate('/chat');
        break;
      case 3: // Profile
        setIsTargetsModalOpen(true);
        break;
      default:
        break;
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };



  // Sync bottom navigation with current route
  useEffect(() => {
    if (isMobile) {
      const path = location.pathname;
      if (path === '/dashboard') {
        setBottomNavValue(0);
      } else if (path === '/chat') {
        setBottomNavValue(2);
      } else if (path === '/Journal') {
        setBottomNavValue(0); // Notes are part of dashboard
      } else if (path === '/analysis') {
        setBottomNavValue(0); // Psychology is part of dashboard
      }
    }
  }, [location.pathname, isMobile]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'var(--background-color)' }}>
      {/* Navbar */}
      <AppBar position="sticky" sx={{ 
        bgcolor: 'var(--primary-color)',
        boxShadow: scrollY > 10 ? '0 8px 32px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.3)',
        transform: scrollY > 50 ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'box-shadow 0.3s ease-out, transform 0.3s ease-out'
      }}>
        <Toolbar>
          <MdDashboard sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Traders Journal
          </Typography>
          
          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <Button
                color="inherit"
                startIcon={<MdDashboard />}
                onClick={(e) => {
                  handleClickAnimation(e.currentTarget);
                  scrollToSection('overview');
                }}
                sx={{ mx: 1, '&:hover': { bgcolor: 'var(--primary-hover)' } }}
              >
                Dashboard
              </Button>
              <Button
                color="inherit"
                startIcon={<FaPlus />}
                onClick={(e) => {
                  handleClickAnimation(e.currentTarget);
                  scrollToSection('trade-form');
                }}
                sx={{ mx: 1, '&:hover': { bgcolor: 'var(--primary-hover)' } }}
              >
                Add Trade
              </Button>
              <Button
                color="inherit"
                startIcon={<FaChartLine />}
                onClick={(e) => {
                  handleClickAnimation(e.currentTarget);
                  scrollToSection('charts');
                }}
                sx={{ mx: 1, '&:hover': { bgcolor: 'var(--primary-hover)' } }}
              >
                Charts
              </Button>
              <Button
                color="inherit"
                startIcon={<MdAssessment />}
                onClick={(e) => {
                  handleClickAnimation(e.currentTarget);
                  scrollToSection('insights');
                }}
                sx={{ mx: 1, '&:hover': { bgcolor: 'var(--primary-hover)' } }}
              >
                Insights
              </Button>
              <Button
                color="inherit"
                startIcon={<MdPsychology />}
                component={Link}
                to="/analysis"
                sx={{ mx: 1, '&:hover': { bgcolor: 'var(--primary-hover)' } }}
              >
                Psychology Check
              </Button>
              <Button
                color="inherit"
                startIcon={<Notes />}
                component={Link}
                to="/Journal"
                sx={{ mx: 1, '&:hover': { bgcolor: 'var(--primary-hover)' } }}
              >
                Notes
              </Button>
              <Button
                color="inherit"
                startIcon={<ChatIcon />}
                component={Link}
                to="/chat"
                sx={{ mx: 1, '&:hover': { bgcolor: 'var(--primary-hover)' } }}
              >
                Chat
              </Button>
              <Button
                color="inherit"
                startIcon={<FaCog />}
                onClick={(e) => {
                  handleClickAnimation(e.currentTarget);
                  setIsTargetsModalOpen(true);
                }}
                sx={{ mx: 1, '&:hover': { bgcolor: 'var(--primary-hover)' } }}
              >
                Targets
              </Button>
              
              
            </>
          )}
          
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              onClick={toggleMobileMenu}
              sx={{ ml: 'auto' }}
            >
              <FaBars />
            </IconButton>
          )}
          
          {/* Network Status Indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: isOnline ? 'success.main' : 'error.main',
                mr: 1,
                animation: isOnline ? 'none' : 'pulse 2s infinite'
              }}
            />
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'inherit',
                opacity: 0.8,
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 }
                }
              }}
            >
              {isOnline ? 'Online' : 'Offline'}
            </Typography>
          </Box>
          
          <Tooltip title="Logout">
            <IconButton 
              color="inherit" 
              onClick={(e) => {
                handleClickAnimation(e.currentTarget);
                handleLogout();
              }} 
              sx={{ ml: 2, '&:hover': { bgcolor: 'var(--primary-hover)' } }}
            >
              <FaSignOutAlt />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'var(--primary-color)',
            color: 'white'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Menu
            </Typography>
            <IconButton color="inherit" onClick={closeMobileMenu}>
              <MdClose />
            </IconButton>
          </Box>
          
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  closeMobileMenu();
                  scrollToSection('overview');
                }}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <MdDashboard />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  closeMobileMenu();
                  scrollToSection('trade-form');
                }}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <FaPlus />
                </ListItemIcon>
                <ListItemText primary="Add Trade" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  closeMobileMenu();
                  scrollToSection('charts');
                }}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <FaChartLine />
                </ListItemIcon>
                <ListItemText primary="Charts" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  closeMobileMenu();
                  scrollToSection('insights');
                }}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <MdAssessment />
                </ListItemIcon>
                <ListItemText primary="Insights" />
              </ListItemButton>
            </ListItem>
            
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
            
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/analysis"
                onClick={closeMobileMenu}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <MdPsychology />
                </ListItemIcon>
                <ListItemText primary="Psychology Check" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/Journal"
                onClick={closeMobileMenu}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <Notes />
                </ListItemIcon>
                <ListItemText primary="Notes" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/chat"
                onClick={closeMobileMenu}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <ChatIcon />
                </ListItemIcon>
                <ListItemText primary="Chat" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  closeMobileMenu();
                  setIsTargetsModalOpen(true);
                }}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <FaCog />
                </ListItemIcon>
                <ListItemText primary="Targets" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, flex: 1 }}>
        {/* Network Status Alert */}
        {!isOnline && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            }
          >
            You are currently offline. Some features may not work properly. 
            Please check your internet connection.
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Account Blown Alert */}
        {accountBlown && (
          <Box sx={{ mb: 3 }}>
            <Alert
              severity="error"
              sx={{
                borderLeft: '4px solid #f44336',
                bgcolor: 'error.light'
              }}
            >
              <Typography variant="h6" gutterBottom>
                ⚠️ Account Alert: Significant Losses Detected
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your account has lost more than 80% of its initial balance. This is a critical situation that requires immediate attention.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Recommendations:</strong>
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <InfoIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Stop trading immediately and review your strategy" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <InfoIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Download your trading data for analysis before resetting" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <InfoIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Consider reducing position sizes and improving risk management" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={exportCSV}
                  size="small"
                >
                  Download CSV Data
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={exportPDF}
                  size="small"
                >
                  Download PDF Report
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setIsResetModalOpen(true)}
                  size="small"
                >
                  Reset Account
                </Button>
              </Box>
            </Alert>
          </Box>
        )}



       {/* Overview Section - Collapsible */}
    <Box id="overview" sx={{ mb: 4 }}>
      <Paper
        sx={{
          p: 2,
          cursor: 'pointer',
          mb: 2,
          bgcolor: 'var(--card-background)',
          border: `1px solid var(--border-color)`,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
        onClick={() => setShowTradingOverview(!showTradingOverview)}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              mb: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'text.primary',
            }}
          >
            <FaChartLine style={{ fontSize: '1.5rem' }} />
            Trading Overview
          </Typography>
          <IconButton size="small">
            {showTradingOverview ? <MdClose /> : <FaPlus />}
          </IconButton>
        </Box>
      </Paper>
      {showTradingOverview && analysis && (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={2}>
            <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={300}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'var(--card-background)',
                  border: `1px solid var(--border-color)`,
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Account Balance
                </Typography>
                <Typography variant="h4" color="primary">
                  ${(profitTargets.initialBalance + (analysis.totalPnl || 0)).toFixed(2)}
                </Typography>
              </Paper>
            </Slide>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={400}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'var(--card-background)',
                  border: `1px solid var(--border-color)`,
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Total P&L
                </Typography>
                <Typography
                  variant="h4"
                  color={analysis.totalPnl >= 0 ? 'success.main' : 'error.main'}
                >
                  {(analysis.totalPnl || 0) >= 0 ? '+' : ''}${Math.abs(analysis.totalPnl || 0).toFixed(2)}
                </Typography>
              </Paper>
            </Slide>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={500}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'var(--card-background)',
                  border: `1px solid var(--border-color)`,
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  P&L %
                </Typography>
                <Typography
                  variant="h4"
                  color={analysis.totalPnl >= 0 ? 'success.main' : 'error.main'}
                >
                  {(analysis.totalPnl || 0) >= 0 ? '+' : ''}{((analysis.totalPnl || 0) / profitTargets.initialBalance * 100).toFixed(2)}%
                </Typography>
              </Paper>
            </Slide>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={600}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'var(--card-background)',
                  border: `1px solid var(--border-color)`,
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Win Rate
                </Typography>
                <Typography variant="h4" color="primary">
                  {(analysis.winRate || 0).toFixed(1)}%
                </Typography>
              </Paper>
            </Slide>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={700}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'var(--card-background)',
                  border: `1px solid var(--border-color)`,
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Total Trades
                </Typography>
                <Typography variant="h4">{trades.length}</Typography>
              </Paper>
            </Slide>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={800}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'var(--card-background)',
                  border: `1px solid var(--border-color)`,
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Avg Duration
                </Typography>
                <Typography variant="h4">
                  {analysis.avgDuration ? `${analysis.avgDuration.toFixed(0)}m` : 'N/A'}
                </Typography>
              </Paper>
            </Slide>
          </Grid>
        </Grid>
      )}
    </Box>


        {/* Psychology Analysis Alert */}
        {psychologyAnalysis && (
          <Box sx={{ 
            mb: 3, 
            animation: 'slideInUp 1s ease-out 1.8s both',
            transform: `translateY(${Math.min(scrollY * 0.08, 15)}px)`,
            opacity: isScrolling ? 0.8 : 1,
            transition: 'transform 0.3s ease-out, opacity 0.2s ease-out'
          }}>
            <Alert
              severity={
                psychologyAnalysis.riskLevel === 'CRITICAL' ? 'error' :
                psychologyAnalysis.riskLevel === 'HIGH' ? 'warning' :
                psychologyAnalysis.riskLevel === 'MEDIUM' ? 'info' : 'success'
              }
              sx={{
                borderLeft: `4px solid ${
                  psychologyAnalysis.riskLevel === 'CRITICAL' ? '#f44336' :
                  psychologyAnalysis.riskLevel === 'HIGH' ? '#ff9800' :
                  psychologyAnalysis.riskLevel === 'MEDIUM' ? '#2196f3' : '#4caf50'
                }`,
                bgcolor: psychologyAnalysis.riskLevel === 'CRITICAL' ? 'error.light' :
                         psychologyAnalysis.riskLevel === 'HIGH' ? 'warning.light' :
                         psychologyAnalysis.riskLevel === 'MEDIUM' ? 'info.light' : 'success.light'
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                animation: 'fadeInDown 0.8s ease-out 2.0s both'
              }}>
                <FaBrain sx={{ animation: 'pulse 2s ease-in-out infinite' }} />
                Psychology Check: {psychologyAnalysis.shouldTrade ? '✅ CLEARED TO TRADE' : '❌ DO NOT TRADE'}
              </Typography>
              <Typography variant="body1" sx={{ 
                mb: 1,
                animation: 'fadeInUp 0.8s ease-out 2.2s both'
              }}>
                <strong>Psychology Score:</strong> {psychologyAnalysis.psychologyScore}/100 |
                <strong> Risk Level:</strong> {psychologyAnalysis.riskLevel}
              </Typography>
              <Typography variant="body2" sx={{ 
                mb: 2,
                animation: 'fadeInUp 0.8s ease-out 2.4s both'
              }}>
                {psychologyAnalysis.tradingAdvice}
              </Typography>
              
              {psychologyAnalysis.warnings.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaExclamationTriangle color="#ff9800" />
                    Warnings:
                  </Typography>
                  <List dense>
                    {psychologyAnalysis.warnings.map((warning, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <MdWarning color="warning" />
                        </ListItemIcon>
                        <ListItemText primary={warning} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* Add button to complete psychology assessment if no data */}
              {psychologyAnalysis.psychologyScore === 0 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to="/analysis"
                    startIcon={<PsychologyIcon />}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Complete Daily Psychology Assessment
                  </Button>
                </Box>
              )}

              {/* Add refresh button for psychology analysis */}
              <Box sx={{ 
                mt: 2, 
                textAlign: 'center',
                animation: 'fadeInUp 0.8s ease-out 2.6s both'
              }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={(e) => {
                    handleClickAnimation(e.currentTarget);
                    fetchPsychologyAnalysis(true);
                  }}
                  startIcon={<MdPsychology />}
                  size="small"
                  sx={{ 
                    mr: 1,
                    animation: 'bounceIn 0.8s ease-out 2.8s both',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  Force Refresh Psychology Analysis
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  component={Link}
                  to="/analysis"
                  startIcon={<MdPsychology />}
                  size="small"
                  onClick={(e) => handleClickAnimation(e.currentTarget)}
                  sx={{ 
                    mr: 1,
                    animation: 'bounceIn 0.8s ease-out 3.0s both',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  View Psychology Assessment
                </Button>
                {psychologyAnalysis.detailedAnalysis && (
                  <Button
                    variant="outlined"
                    color="info"
                    onClick={(e) => {
                      handleClickAnimation(e.currentTarget);
                      setShowDetailedPsychologyModal(true);
                    }}
                    startIcon={<FaBrain />}
                    size="small"
                    sx={{
                      animation: 'bounceIn 0.8s ease-out 3.2s both',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    Detailed Psychology Analysis
                  </Button>
                )}
              </Box>

              {psychologyAnalysis.recommendations.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaLightbulb color="#2196f3" />
                    Recommendations:
                  </Typography>
                  <List dense>
                    {psychologyAnalysis.recommendations.map((rec, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <MdInfo color="info" />
                        </ListItemIcon>
                        <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {psychologyAnalysis.consequences && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
                    💸 Consequences of Trading Against Psychology Check:
                  </Typography>
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Trades Made:</strong> {psychologyAnalysis.consequences.tradesCount}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Total P&L:</strong> ${psychologyAnalysis.consequences.totalPnl.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Win Rate:</strong> {psychologyAnalysis.consequences.winRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Expected Loss:</strong> ${psychologyAnalysis.consequences.expectedLoss.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      {psychologyAnalysis.consequences.message}
                    </Typography>
                  </Box>
                </>
              )}
            </Alert>
          </Box>
        )}




        {/* Weekly Target Popup Modal - Show from Friday to Sunday */}
        <Modal
          open={showWeeklyTargetPopup && weeklyCheckResult?.shouldShowPopup}
          onClose={() => {
            setShowWeeklyTargetPopup(false);
            // Save dismissal state to localStorage
            const currentWeek = new Date().toISOString().split('T')[0].slice(0, 10);
            localStorage.setItem('weeklyTargetPopupDismissed', currentWeek);
          }}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={showWeeklyTargetPopup && weeklyCheckResult}>
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: '80%', md: '60%' },
              maxWidth: 600,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {weeklyCheckResult?.targetMet ? (
                    <>
                      <FaCheckCircle color="#4caf50" size={24} />
                      Weekly Target Achieved!
                    </>
                  ) : (
                    <>
                      <FaBullseye color="#ff9800" size={24} />
                      Weekly Target Not Met
                    </>
                  )}
                </Typography>
                <IconButton onClick={() => {
                  setShowWeeklyTargetPopup(false);
                  // Save dismissal state to localStorage
                  const currentWeek = new Date().toISOString().split('T')[0].slice(0, 10);
                  localStorage.setItem('weeklyTargetPopupDismissed', currentWeek);
                }}>
                  <MdClose />
                </IconButton>
              </Box>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                You've made ${weeklyCheckResult?.recentPnl.toFixed(2) || '0.00'} this week (Monday to Sunday SA time).
                {weeklyCheckResult?.targetMet
                  ? " Great job meeting your target!"
                  : ` Your target was $${weeklyCheckResult?.target.toFixed(2) || '0.00'}.`}
              </Typography>

              {/* Psychology Analysis Section */}
              {weeklyCheckResult?.psychologyAnalysis && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaBrain color="#1976d2" />
                    Psychology Impact Analysis
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                    {weeklyCheckResult.psychologyAnalysis.impact}
                  </Typography>

                  {weeklyCheckResult.psychologyAnalysis.factors.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Key Factors Identified:
                      </Typography>
                      <List dense>
                        {weeklyCheckResult.psychologyAnalysis.factors.map((factor, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <FaExclamationTriangle color="#ff9800" />
                            </ListItemIcon>
                            <ListItemText primary={factor} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}

                  <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                    Based on {weeklyCheckResult.psychologyChecksCount} psychology assessments and {weeklyCheckResult.weeklyTradesCount} trades this week.
                  </Typography>
                </Box>
              )}
              
              {!weeklyCheckResult?.targetMet && weeklyCheckResult?.recommendations.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaLightbulb color="#ff9800" />
                    Recommendations to improve your trading:
                  </Typography>
                  <List dense>
                    {weeklyCheckResult.recommendations.map((rec, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <MdInfo color="warning" />
                        </ListItemIcon>
                        <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
              
                             <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                 <Button
                   variant="contained"
                   onClick={() => {
                     setShowWeeklyTargetPopup(false);
                     // Save dismissal state to localStorage
                     const currentWeek = new Date().toISOString().split('T')[0].slice(0, 10);
                     localStorage.setItem('weeklyTargetPopupDismissed', currentWeek);
                   }}
                   startIcon={<MdClose />}
                 >
                   Close
                 </Button>
               </Box>
            </Box>
          </Fade>
        </Modal>


        {/* Risk Management Section - Collapsible */}
        <Box sx={{ mb: 4 }}>
          <Paper sx={{ p: 2, cursor: 'pointer', mb: 2 }} onClick={() => setShowRiskManagement(!showRiskManagement)}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                Risk Management
              </Typography>
              <IconButton size="small">
                {showRiskManagement ? <MdClose /> : <FaPlus />}
              </IconButton>
            </Box>
          </Paper>
          {showRiskManagement && (
            <RiskManagement trades={trades} analysis={analysis} />
          )}
        </Box>

        {/* Trade Form Section */}
        <Box id="trade-form" sx={{ mb: 4 }}>
          <TradeForm 
            onAddTrade={handleAddTrade} 
            onUpdateTrade={handleUpdateTrade}
            editingTrade={editingTrade}
            onCancelEdit={handleCancelEdit}
          />
        </Box>



        {/* Trade Log - Collapsible */}
        <Box sx={{ mb: 4 }}>
          <Paper sx={{ p: 2, cursor: 'pointer', mb: 2 }} onClick={() => setShowTradeLog(!showTradeLog)}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                Trade Log
              </Typography>
              <IconButton size="small">
                {showTradeLog ? <MdClose /> : <FaPlus />}
              </IconButton>
            </Box>
          </Paper>
          {showTradeLog && (
            <Paper sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={trades}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              getRowId={(row) => row._id || row.tradeId}
              loading={loading}
              sx={{
                '& .profit': {
                  color: 'success.main',
                  fontWeight: 'bold',
                },
                '& .loss': {
                  color: 'error.main',
                  fontWeight: 'bold',
                },
              }}
            />
          </Paper>
            )}
        </Box>

        {/* Charts Section */}
        <Box id="charts" sx={{ mb: 4 }}>
          <TradeCharts trades={trades} profitTargets={profitTargets} />
        </Box>

        {/* Insights Section */}
        <Box id="insights" sx={{ mb: 4 }}>
          <Insights analysis={analysis} trades={trades} />
        </Box>

        {/* Export Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Export Data
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={exportCSV}>
              Export CSV
            </Button>
            <Button variant="contained" onClick={exportPDF}>
              Export PDF
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteAllTrades}>
              Delete All Trades
            </Button>
          </Box>
        </Box>
      </Container>
      
      {/* Profit Targets Modal */}
      <Modal
        open={isTargetsModalOpen}
        onClose={() => setIsTargetsModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isTargetsModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '60%' },
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h5" gutterBottom>
              Set Profit Targets
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Initial Balance: ${profitTargets.initialBalance}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="number"
                    value={newProfitTargets.initialBalance}
                    onChange={(e) => setNewProfitTargets({...newProfitTargets, initialBalance: parseFloat(e.target.value) || 0})}
                    size="small"
                    variant="outlined"
                    InputProps={{
                      inputProps: { min: 0, step: 1000 }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => updateProfitTarget('initialBalance')}
                    size="small"
                  >
                    Set
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Daily Profit Target: ${profitTargets.profitTarget}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="number"
                    value={newProfitTargets.profitTarget}
                    onChange={(e) => setNewProfitTargets({...newProfitTargets, profitTarget: parseFloat(e.target.value) || 0})}
                    size="small"
                    variant="outlined"
                    InputProps={{
                      inputProps: { min: 0, step: 100 }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => updateProfitTarget('profitTarget')}
                    size="small"
                  >
                    Set
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Weekly Profit Target: ${profitTargets.weeklyProfitTarget}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="number"
                    value={newProfitTargets.weeklyProfitTarget}
                    onChange={(e) => setNewProfitTargets({...newProfitTargets, weeklyProfitTarget: parseFloat(e.target.value) || 0})}
                    size="small"
                    variant="outlined"
                    InputProps={{
                      inputProps: { min: 0, step: 100 }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => updateProfitTarget('weeklyProfitTarget')}
                    size="small"
                  >
                    Set
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Monthly Profit Target: ${profitTargets.monthlyProfitTarget}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="number"
                    value={newProfitTargets.monthlyProfitTarget}
                    onChange={(e) => setNewProfitTargets({...newProfitTargets, monthlyProfitTarget: parseFloat(e.target.value) || 0})}
                    size="small"
                    variant="outlined"
                    InputProps={{
                      inputProps: { min: 0, step: 100 }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => updateProfitTarget('monthlyProfitTarget')}
                    size="small"
                  >
                    Set
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Quarterly Profit Target: ${profitTargets.quarterlyProfitTarget}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="number"
                    value={newProfitTargets.quarterlyProfitTarget}
                    onChange={(e) => setNewProfitTargets({...newProfitTargets, quarterlyProfitTarget: parseFloat(e.target.value) || 0})}
                    size="small"
                    variant="outlined"
                    InputProps={{
                      inputProps: { min: 0, step: 100 }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => updateProfitTarget('quarterlyProfitTarget')}
                    size="small"
                  >
                    Set
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Yearly Profit Target: ${profitTargets.yearlyProfitTarget}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="number"
                    value={newProfitTargets.yearlyProfitTarget}
                    onChange={(e) => setNewProfitTargets({...newProfitTargets, yearlyProfitTarget: parseFloat(e.target.value) || 0})}
                    size="small"
                    variant="outlined"
                    InputProps={{
                      inputProps: { min: 0, step: 100 }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => updateProfitTarget('yearlyProfitTarget')}
                    size="small"
                  >
                    Set
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsTargetsModalOpen(false)}
                  >
                    Close
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Modal>
      
      {/* Trade Details Modal */}
      <Modal
        open={isTradeModalOpen}
        onClose={() => setIsTradeModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isTradeModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '60%' },
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}>
            {selectedTrade && (
              <>
                <Typography variant="h5" gutterBottom>
                  Trade Details
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Trade Information
                    </Typography>
                    <Typography><strong>Trade ID:</strong> {selectedTrade.tradeId}</Typography>
                    <Typography><strong>Symbol:</strong> {selectedTrade.symbol}</Typography>
                    <Typography><strong>Side:</strong> {selectedTrade.side}</Typography>
                    <Typography><strong>Entry:</strong> {selectedTrade.entry}</Typography>
                    <Typography><strong>Exit:</strong> {selectedTrade.exit}</Typography>
                    <Typography><strong>Quantity:</strong> {selectedTrade.qty}</Typography>
                    <Typography><strong>P&L:</strong> ${selectedTrade.pnl?.toFixed(2)}</Typography>
                    <Typography><strong>Status:</strong> {selectedTrade.status}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Trade Dates
                    </Typography>
                    <Typography><strong>Open Date:</strong> {new Date(selectedTrade.openDate).toLocaleString()}</Typography>
                    <Typography><strong>Close Date:</strong> {new Date(selectedTrade.closeDate).toLocaleString()}</Typography>
                    
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Risk Management
                    </Typography>
                    <Typography><strong>Stop Loss:</strong> {selectedTrade.stopLoss || 'N/A'}</Typography>
                    <Typography><strong>Take Profit:</strong> {selectedTrade.takeProfit || 'N/A'}</Typography>
                    <Typography><strong>Risk/Reward Ratio:</strong> {selectedTrade.riskRewardRatio || 'N/A'}</Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Analysis
                    </Typography>
                    <Typography><strong>Market Condition:</strong> {selectedTrade.marketCondition || 'N/A'}</Typography>
                    <Typography><strong>Setup Quality:</strong> {selectedTrade.setupQuality || 'N/A'}</Typography>
                    <Typography><strong>Notes:</strong> {selectedTrade.notes || 'N/A'}</Typography>
                  </Grid>
                  
                  {selectedTrade.beforeImage && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Before Trade
                      </Typography>
                      <Box
                        component="img"
                        src={selectedTrade.beforeImage}
                        alt="Before trade"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 300,
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          p: 1,
                          cursor: 'pointer'
                        }}
                       onClick={() => handleMediaClick({ type: 'image', src: selectedTrade.beforeImage })}
                     />
                   </Grid>
                 )}
                  
                  {selectedTrade.afterImage && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        After Trade
                      </Typography>
                      <Box
                        component="img"
                        src={selectedTrade.afterImage}
                        alt="After trade"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 300,
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          p: 1,
                          cursor: 'pointer'
                        }}
                       onClick={() => handleMediaClick({ type: 'image', src: selectedTrade.afterImage })}
                     />
                   </Grid>
                 )}
                  
                  {selectedTrade.video && (
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Trade Video
                      </Typography>
                      <Box
                        component="video"
                        src={selectedTrade.video}
                        controls
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 300,
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          p: 1,
                          cursor: 'pointer'
                        }}
                       onClick={() => handleMediaClick({ type: 'video', src: selectedTrade.video })}
                     />
                   </Grid>
                 )}
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setIsTradeModalOpen(false)}
                      >
                        Close
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </Fade>
      </Modal>

     {/* Media Viewer Modal */}
     <Modal
       open={isMediaModalOpen}
       onClose={handleCloseMediaModal}
       closeAfterTransition
       BackdropComponent={Backdrop}
       BackdropProps={{
         timeout: 500,
       }}
     >
       <Fade in={isMediaModalOpen}>
         <Box
           sx={{
             position: 'absolute',
             top: '50%',
             left: '50%',
             transform: 'translate(-50%, -50%)',
             width: '90vw',
             height: '90vh',
             bgcolor: 'background.paper',
             boxShadow: 24,
             p: 4,
             display: 'flex',
             justifyContent: 'center',
             alignItems: 'center',
           }}
         >
           {selectedMedia?.type === 'image' && (
             <Box
               component="img"
               src={selectedMedia.src}
               alt="Full view"
               sx={{
                 maxWidth: '100%',
                 maxHeight: '100%',
                 objectFit: 'contain',
               }}
             />
           )}
           {selectedMedia?.type === 'video' && (
             <Box
               component="video"
               src={selectedMedia.src}
               controls
               autoPlay
               sx={{
                 maxWidth: '100%',
                 maxHeight: '100%',
                 objectFit: 'contain',
               }}
             />
           )}
         </Box>
       </Fade>
     </Modal>

     {/* Welcome Popup Modal */}
     <Modal
       open={showWelcomePopup}
       onClose={() => setShowWelcomePopup(false)}
       closeAfterTransition
       BackdropComponent={Backdrop}
       BackdropProps={{
         timeout: 800,
       }}
     >
       <Fade in={showWelcomePopup} timeout={800}>
         <Box sx={{
           position: 'absolute',
           top: '50%',
           left: '50%',
           transform: 'translate(-50%, -50%)',
           width: { xs: '90%', sm: '450px' },
           bgcolor: 'background.paper',
           borderRadius: 4,
           boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)',
           p: 0,
           textAlign: 'center',
           overflow: 'hidden',
           '&::before': {
             content: '""',
             position: 'absolute',
             top: 0,
             left: 0,
             right: 0,
             height: '4px',
             background: 'linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
             animation: 'shimmer 2s ease-in-out infinite'
           }
         }}>
           {/* Animated background particles */}
           <Box sx={{
             position: 'absolute',
             top: 0,
             left: 0,
             right: 0,
             bottom: 0,
             overflow: 'hidden',
             pointerEvents: 'none',
             '&::before, &::after': {
               content: '""',
               width: '6px',
               height: '6px',
               borderRadius: '50%',
               background: 'rgba(102, 126, 234, 0.3)',
               animation: 'float 3s ease-in-out infinite'
             },
             '&::before': {
               top: '20%',
               left: '10%',
               animationDelay: '0s'
             },
             '&::after': {
               top: '60%',
               right: '15%',
               animationDelay: '1.5s'
             }
           }} />
           
           {/* Main content with staggered animations */}
           <Box sx={{ p: 4, position: 'relative', zIndex: 1 }}>
             {/* Icon with bounce animation */}
             <Box sx={{
               display: 'flex',
               justifyContent: 'center',
               mb: 2,
               animation: 'bounceIn 1s ease-out 0.2s both'
             }}>
               <Box sx={{
                 width: 80,
                 height: 80,
                 borderRadius: '50%',
                 background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
                 animation: 'pulse 2s ease-in-out infinite'
               }}>
                 <MdDashboard sx={{ 
                   fontSize: 40, 
                   color: 'white',
                   animation: 'rotate 3s linear infinite'
                 }} />
               </Box>
             </Box>
             
             {/* Title with slide-in animation */}
             <Typography variant="h4" gutterBottom sx={{ 
               fontWeight: 'bold', 
               color: 'primary.main',
               animation: 'slideInDown 0.8s ease-out 0.4s both',
               textShadow: '0 2px 4px rgba(0,0,0,0.1)'
             }}>
               Welcome back!
             </Typography>
             
             {/* Username with fade-in animation */}
             <Typography variant="h5" sx={{ 
               color: 'text.secondary',
               mb: 2,
               animation: 'fadeInUp 0.8s ease-out 0.6s both',
               fontWeight: 500
             }}>
               Hi @ {user?.email?.split('@')[0]}
             </Typography>
             
             {/* Message with scale animation */}
             <Typography variant="body1" sx={{ 
               color: 'text.secondary',
               animation: 'scaleIn 0.8s ease-out 0.8s both',
               fontSize: '1.1rem'
             }}>
               Ready to track your trading performance?
             </Typography>
             
             {/* Animated progress bar */}
             <Box sx={{
               mt: 3,
               height: '3px',
               background: 'rgba(0,0,0,0.1)',
               borderRadius: '2px',
               overflow: 'hidden',
               animation: 'slideInUp 0.8s ease-out 1s both'
             }}>
               <Box sx={{
                 height: '100%',
                 background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                 borderRadius: '2px',
                 animation: 'progressFill 4s ease-out 1.2s forwards'
               }} />
             </Box>
           </Box>
           
           {/* CSS Animations */}
           <style jsx>{`
             @keyframes bounceIn {
               0% {
                 opacity: 0;
                 transform: scale(0.3);
               }
               50% {
                 opacity: 1;
                 transform: scale(1.05);
               }
               70% {
                 transform: scale(0.9);
               }
               100% {
                 opacity: 1;
                 transform: scale(1);
               }
             }
             
             @keyframes slideInDown {
               0% {
                 opacity: 0;
                 transform: translateY(-30px);
               }
               100% {
                 opacity: 1;
                 transform: translateY(0);
               }
             }
             
             @keyframes fadeInUp {
               0% {
                 opacity: 0;
                 transform: translateY(20px);
               }
               100% {
                 opacity: 1;
                 transform: translateY(0);
               }
             }
             
             @keyframes scaleIn {
               0% {
                 opacity: 0;
                 transform: scale(0.8);
               }
               100% {
                 opacity: 1;
                 transform: scale(1);
               }
             }
             
             @keyframes slideInUp {
               0% {
                 opacity: 0;
                 transform: translateY(20px);
               }
               100% {
                 opacity: 1;
                 transform: translateY(0);
               }
             }
             
             @keyframes progressFill {
               0% {
                 width: 0%;
               }
               100% {
                 width: 100%;
               }
             }
             
             @keyframes pulse {
               0%, 100% {
                 transform: scale(1);
                 box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
               }
               50% {
                 transform: scale(1.05);
                 box-shadow: 0 15px 40px rgba(102, 126, 234, 0.6);
               }
             }
             
             @keyframes rotate {
               0% {
                 transform: rotate(0deg);
               }
               100% {
                 transform: rotate(360deg);
               }
             }
             
             @keyframes shimmer {
               0% {
                 transform: translateX(-100%);
               }
               100% {
                 transform: translateX(100%);
               }
             }
             
             @keyframes float {
               0%, 100% {
                 transform: translateY(0px) rotate(0deg);
                 opacity: 0.3;
               }
               50% {
                 transform: translateY(-20px) rotate(180deg);
                 opacity: 0.8;
               }
             }
                       `}</style>
          </Box>
        </Fade>
      </Modal>

      {/* Global CSS Animations for Dashboard */}
      <style jsx global>{`
        @keyframes fadeInLeft {
          0% {
            opacity: 0;
            transform: translateX(-30px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInRight {
          0% {
            opacity: 0;
            transform: translateX(30px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0, -8px, 0);
          }
          70% {
            transform: translate3d(0, -4px, 0);
          }
          90% {
            transform: translate3d(0, -2px, 0);
          }
        }

        @keyframes slideInFromBottom {
          0% {
            opacity: 0;
            transform: translateY(50px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes rotateIn {
          0% {
            opacity: 0;
            transform: rotate(-180deg) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: rotate(0deg) scale(1);
          }
        }

        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 5px rgba(102, 126, 234, 0.3);
          }
          50% {
            box-shadow: 0 0 20px rgba(102, 126, 234, 0.6);
          }
        }

        /* Scroll-triggered animations */
        .scroll-animate {
          transition: all 0.3s ease-out;
        }

        .scroll-animate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }

        /* Click animations */
        .click-animate {
          transition: transform 0.1s ease-out;
        }

        .click-animate:active {
          transform: scale(0.95);
        }

        /* Parallax effect */
        .parallax {
          transition: transform 0.3s ease-out;
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Enhanced focus states */
        button:focus, 
        [role="button"]:focus {
          outline: 2px solid rgba(102, 126, 234, 0.5);
          outline-offset: 2px;
        }
      `}</style>

     {/* Account Reset Modal */}
       <Modal
            open={isResetModalOpen}
            onClose={() => setIsResetModalOpen(false)}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={isResetModalOpen}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '80%', md: '50%' },
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                }}>
                    <Typography variant="h5" gutterBottom color="error">
                        ⚠️ Reset Account - Data Loss Warning
                    </Typography>

                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body1" gutterBottom>
                            <strong>IMPORTANT:</strong> This action will permanently delete all your trading data!
                        </Typography>
                        <Typography variant="body2">
                            We do not keep backups of your previous trading performance. Once you reset, all trades,
                            statistics, and historical data will be lost forever.
                        </Typography>
                    </Alert>

                    <Typography variant="h6" gutterBottom>
                        Before you proceed:
                    </Typography>

                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <InfoIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Download your trading data"
                                secondary="Export your trades as CSV or PDF for future reference and analysis"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <InfoIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Review your performance"
                                secondary="Analyze what went wrong to avoid repeating the same mistakes"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <InfoIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Plan your comeback"
                                secondary="Consider adjusting your strategy, risk management, and position sizing"
                            />
                        </ListItem>
                    </List>

                    <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={exportCSV}
                            size="small"
                        >
                            Download CSV
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={exportPDF}
                            size="small"
                        >
                            Download PDF
                        </Button>
                    </Box>

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setIsResetModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleResetAccount}
                        >
                            I understand - Reset Account
                        </Button>
                    </Box>
                </Box>
            </Fade>
        </Modal>
        
        {/* Mental Capacity Check Modal */}
        <Modal
          open={showMentalCheck}
          onClose={() => setShowMentalCheck(false)}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={showMentalCheck}>
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: '80%', md: '60%' },
              maxHeight: '90vh',
              overflowY: 'auto',
              bgcolor: 'background.paper',
              border: '2px solid #000',
              boxShadow: 24,
              p: 4,
            }}>
              <Typography variant="h5" gutterBottom>
                Mental Capacity Check
              </Typography>
              
              {lastTrade && (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Trade Result: {lastTrade.status === 'Win' ? '✅ Winner' : '❌ Loser'}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {lastTrade.status === 'Win'
                      ? "Congratulations on your winning trade! However, it's important to check your mental state before taking another trade."
                      : "Don't worry about the loss. Let's assess your mental state to determine if you're ready for another trade."}
                  </Typography>
                </>
              )}
              
              <Typography variant="body1" sx={{ mb: 3 }}>
                Please answer the following questions honestly to assess your current mental capacity for trading:
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">How are you feeling right now?</Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowMentalCheck(false);
                      alert("Based on your mental state, we recommend taking a break before your next trade.");
                    }}
                    sx={{ mr: 2, mb: 1 }}
                  >
                    Frustrated/Emotional
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowMentalCheck(false);
                      alert("Based on your mental state, we recommend taking a break before your next trade.");
                    }}
                    sx={{ mr: 2, mb: 1 }}
                  >
                    Tired/Fatigued
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowMentalCheck(false);
                      alert("Based on your mental state, you seem ready for another trade.");
                    }}
                    sx={{ mr: 2, mb: 1 }}
                  >
                    Calm/Focused
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowMentalCheck(false);
                      alert("Based on your mental state, you seem ready for another trade.");
                    }}
                    sx={{ mr: 2, mb: 1 }}
                  >
                    Confident/Positive
                  </Button>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6">Are you ready for another trade?</Typography>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      setShowMentalCheck(false);
                      alert("Based on your mental state, we strongly recommend taking a break before your next trade. Consider reviewing your strategy or taking a walk to reset.");
                    }}
                    sx={{ mr: 2, mt: 1 }}
                  >
                    Not Ready - Take a Break
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      setShowMentalCheck(false);
                      alert("Based on your mental state, you seem ready for another trade. Remember to stick to your strategy!");
                    }}
                    sx={{ mt: 1 }}
                  >
                    Ready - Continue Trading
                  </Button>
                </Grid>
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowMentalCheck(false)}
                >
                  Close
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

      {/* Detailed Psychology Analysis Modal */}
      <Modal
        open={showDetailedPsychologyModal}
        onClose={() => setShowDetailedPsychologyModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={showDetailedPsychologyModal}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '80%' },
            maxWidth: 1200,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            overflowY: 'auto'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaBrain />
                Detailed Psychology Analysis
              </Typography>
              <IconButton onClick={() => setShowDetailedPsychologyModal(false)}>
                <MdClose />
              </IconButton>
            </Box>
            {psychologyAnalysis && psychologyAnalysis.detailedAnalysis && (
              <DetailedPsychologyReport analysis={psychologyAnalysis} />
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Floating Action Button for Quick Actions */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000,
          animation: 'bounceIn 1s ease-out 3.4s both'
        }}
      >
        <Tooltip title="Quick Actions" placement="left">
          <IconButton
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: 56,
              height: 56,
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              animation: 'pulse 2s ease-in-out infinite 3.6s',
              '&:hover': {
                bgcolor: 'primary.dark',
                transform: 'scale(1.1)',
                boxShadow: '0 6px 25px rgba(0,0,0,0.4)'
              },
            }}
            onClick={(e) => {
              handleClickAnimation(e.currentTarget);
              setIsTradeModalOpen(true);
            }}
          >
            <FaPlus sx={{ animation: 'rotate 3s linear infinite 3.8s' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Box sx={{ pb: 7 }}> {/* Add padding to prevent content from being hidden behind bottom nav */}
          <BottomNavigation
            value={bottomNavValue}
            onChange={handleBottomNavChange}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              bgcolor: 'var(--primary-color)',
              color: 'white',
              borderTop: '1px solid rgba(255,255,255,0.2)',
              '& .MuiBottomNavigationAction-root': {
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-selected': {
                  color: 'white',
                },
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                }
              }
            }}
          >
            <BottomNavigationAction
              label="Home"
              value={0}
              icon={<FaHome />}
              sx={{ 
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                  fontWeight: 500
                }
              }}
            />
            <BottomNavigationAction
              label="Add Trade"
              value={1}
              icon={<FaPlus />}
              sx={{ 
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                  fontWeight: 500
                }
              }}
            />
            <BottomNavigationAction
              label="Chat"
              value={2}
              icon={<ChatIcon />}
              sx={{ 
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                  fontWeight: 500
                }
              }}
            />
            <BottomNavigationAction
              label="Profile"
              value={3}
              icon={<FaUser />}
              component={Link}
              to="/profile"
              sx={{ 
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                  fontWeight: 500
                }
              }}
            />
          </BottomNavigation>
        </Box>
      )}

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: 'grey.200',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
         Journal © 2025 | Built by ZenoLaunch Team
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Focus on the setup, trust the process | journal To Keep Your Data Performance -ICT
        </Typography>
      </Box>
      
      {/* PWA Install Prompt */}
      <InstallPrompt />
    </Box>
  );
};


export default Dashboard;
